import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Clock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { fetchPartnerMe, PARTNER_STATUS_LABELS } from "../../utils/partnerApi";
import "../marketing/MarketingPage.css";
import { CONTACT_EMAIL } from "../../config/company";

export default function PartnerDossierPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const d = await fetchPartnerMe();
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.message || "Impossible de charger votre dossier.");
    }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("token");
    if (!t) {
      navigate("/auth/login", { replace: true, state: { from: "/partner/mon-dossier" } });
      return;
    }
    load();
  }, [load, navigate]);

  const p = data?.partner;

  return (
    <div className="mkt-shell">
      <div className="mkt-container" style={{ maxWidth: 720 }}>
        <section className="mkt-hero">
          <div className="mkt-hero__inner">
            <div className="mkt-hero__icon">
              <Shield size={34} />
            </div>
            <div className="mkt-hero__copy">
              <h1>Mon dossier ProMeet Partners</h1>
              <p>Suivez l’état de votre vérification et l’accès à ProMeet Missions.</p>
            </div>
          </div>
        </section>

        {err && (
          <div className="mkt-card" style={{ borderColor: "rgba(185,28,28,0.3)", color: "#991b1b" }}>
            {err}
          </div>
        )}

        {!data && !err && <div className="mkt-card">Chargement…</div>}

        {data && !data.hasApplication && (
          <div className="mkt-card mkt-card--accent">
            <h2 className="mkt-section-title" style={{ marginBottom: 12 }}><Clock size={20} /> Aucun dossier</h2>
            <p>Vous n’avez pas encore soumis de candidature partenaire.</p>
            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link className="mkt-btn mkt-btn--brand" to="/auth/signup/partner">Devenir ProMeet Partner</Link>
              <Link className="mkt-btn mkt-btn--outline" to="/missions/dashboard">Retour Missions</Link>
            </div>
          </div>
        )}

        {p && (
          <div className="mkt-card">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              {p.status === "approved" ? (
                <CheckCircle2 size={28} color="var(--color-primary-hover)" />
              ) : p.status === "refused" ? (
                <XCircle size={28} color="#b91c1c" />
              ) : (
                <AlertTriangle size={28} color="#b45309" />
              )}
              <div>
                <div style={{ fontWeight: 900, fontSize: "1.2rem", color: "var(--espresso)" }}>
                  {PARTNER_STATUS_LABELS[p.status] || p.status}
                </div>
                {p.verifiedPartnerBadge && (
                  <span className="pmm-chip pmm-chip--brand" style={{ marginTop: 6 }}>
                    Badge partenaire vérifié
                  </span>
                )}
              </div>
            </div>

            {p.refusalReason && (
              <p style={{ color: "#991b1b", marginBottom: 12 }}><strong>Motif :</strong> {p.refusalReason}</p>
            )}

            <p><strong>Spécialités demandées :</strong> {(p.requestedSpecialties || []).join(", ") || "—"}</p>
            <p><strong>Spécialités validées :</strong> {(p.approvedSpecialties || []).join(", ") || "—"}</p>
            {p.interviewStatus && p.interviewStatus !== "not_scheduled" && (
              <p><strong>Entretien :</strong> {p.interviewStatus}
                {p.interviewDate && ` — ${new Date(p.interviewDate).toLocaleString("fr-FR")}`}
              </p>
            )}

            <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link className="mkt-btn mkt-btn--outline" to="/missions/dashboard">ProMeet Missions</Link>
              <Link className="mkt-btn mkt-btn--outline" to="/help">Besoin d&apos;aide ?</Link>
            </div>
            {["pending_review", "refused"].includes(p.status) && (
              <p style={{ marginTop: 14, fontSize: 13, color: "var(--text-muted)" }}>
                Pour corriger ou compléter votre dossier après un refus, contactez{" "}
                <a className="mkt-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                {" "}ou utilisez l’endpoint <code>PUT /api/partners/update</code> depuis un outil authentifié.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
